import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { DashboardLayout } from "@/pages/Dashboard";
import { useLanguage, useAuth, API } from "@/App";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ListingImage } from "@/components/ListingImage";
import { US_STATES } from "@/lib/listings";
import { SERVICE_CATEGORIES, SUB_LABELS, subLabel, catLabel } from "@/lib/services";
import {
  Search, MapPin, Star, Award, Verified, Sparkles, Phone, MessageCircle,
  Briefcase, Heart, User
} from "lucide-react";
import { toast } from "sonner";

const ProviderCard = ({ p, lang, t, isSaved, onSaveToggle, onClick }) => (
  <div
    className="glass-card rounded-2xl overflow-hidden purple-gold-glow hover:border-yellow-500/60 transition-all"
    data-testid={`provider-card-${p.provider_id}`}
  >
    <button onClick={onClick} className="w-full text-right">
      <div className="relative h-32 bg-slate-900 overflow-hidden">
        <ListingImage path={p.cover_photo} alt="" className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-slate-950 to-transparent" />
        {p.featured && (
          <Badge className="absolute top-2 right-2 bg-gradient-to-r from-purple-500 to-yellow-500 text-white border-0">
            <Sparkles size={10} className="ml-1" /> {t("مميز", "Featured")}
          </Badge>
        )}
      </div>
      <div className="px-4 pb-3 -mt-10 relative">
        <div className="w-20 h-20 rounded-2xl bg-slate-800 border-4 border-slate-950 overflow-hidden mb-3">
          <ListingImage path={p.profile_photo} alt={p.full_name} className="w-full h-full object-cover" />
        </div>
        <div className="flex items-center gap-2 mb-1">
          <h3 className="font-bold text-white">{p.full_name}</h3>
          {p.verified && <Verified className="text-blue-400" size={16} />}
          {p.top_rated && <Award className="text-yellow-400" size={16} />}
        </div>
        {p.business_name && <p className="text-xs text-slate-400 mb-1">{p.business_name}</p>}
        <Badge className="bg-yellow-500/20 text-yellow-400 mb-2">{subLabel(p.profession, lang)}</Badge>
        <p className="text-sm text-slate-400 line-clamp-2 mb-2">{p.description}</p>
        <div className="flex items-center gap-3 text-xs text-slate-400">
          <span className="flex items-center gap-1"><MapPin size={12} className="text-yellow-500" />{p.city}, {p.state}</span>
          {p.rating !== null && (
            <span className="flex items-center gap-1 text-yellow-400">
              <Star size={12} fill="currentColor" /> {p.rating} ({p.review_count})
            </span>
          )}
        </div>
      </div>
    </button>
    <div className="px-4 pb-4 flex gap-2 border-t border-slate-800 pt-3">
      <a href={`tel:${p.phone}`} className="flex-1">
        <Button size="sm" className="w-full btn-primary" data-testid={`provider-call-${p.provider_id}`}>
          <Phone size={14} className="ml-1" /> {t("اتصل", "Call")}
        </Button>
      </a>
      <button
        onClick={(e) => { e.stopPropagation(); onSaveToggle(p.provider_id, !isSaved); }}
        className={`px-3 rounded-lg flex items-center justify-center transition-colors ${isSaved ? "bg-yellow-500 text-black" : "bg-slate-800 text-slate-300"}`}
        data-testid={`provider-save-${p.provider_id}`}
      >
        <Heart size={14} fill={isSaved ? "currentColor" : "none"} />
      </button>
    </div>
  </div>
);

export default function ServicesDirectory() {
  const { t, language } = useLanguage();
  const { token } = useAuth();
  const navigate = useNavigate();
  const [providers, setProviders] = useState([]);
  const [featured, setFeatured] = useState([]);
  const [savedIds, setSavedIds] = useState(new Set());
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    query: "", category: "", profession: "", state: "", city: "", zip_code: "", language: ""
  });

  const load = async (f = filters) => {
    setLoading(true);
    try {
      const params = Object.fromEntries(Object.entries(f).filter(([, v]) => v));
      const res = await axios.get(`${API}/providers`, { params });
      setProviders(res.data);
    } catch (e) { console.error(e); } finally { setLoading(false); }
  };

  const loadSaved = async () => {
    if (!token) return;
    try {
      const res = await axios.get(`${API}/providers/favorites/ids`, { headers: { Authorization: `Bearer ${token}` } });
      setSavedIds(new Set(res.data.ids));
    } catch { /* ignore */ }
  };

  useEffect(() => {
    axios.get(`${API}/providers/featured`).then((r) => setFeatured(r.data)).catch(() => {});
    load();
    loadSaved();
    // eslint-disable-next-line
  }, []);

  const toggleSave = async (id, save) => {
    if (!token) { navigate("/login"); return; }
    try {
      if (save) {
        await axios.post(`${API}/providers/${id}/favorite`, {}, { headers: { Authorization: `Bearer ${token}` } });
        setSavedIds((s) => new Set([...s, id]));
        toast.success(t("تم الحفظ", "Saved"));
      } else {
        await axios.delete(`${API}/providers/${id}/favorite`, { headers: { Authorization: `Bearer ${token}` } });
        setSavedIds((s) => { const n = new Set(s); n.delete(id); return n; });
      }
    } catch { toast.error(t("فشل", "Failed")); }
  };

  const cat = SERVICE_CATEGORIES.find((c) => c.v === filters.category);
  const inputCls = "bg-slate-900 border border-slate-700 rounded-xl px-3 py-3 text-white focus:border-yellow-500 outline-none";

  return (
    <DashboardLayout title={t("دليل الخدمات الاجتماعية", "Social Services Directory")}>
      <div className="space-y-6" data-testid="services-directory">
        <div className="relative glass-card rounded-3xl p-6 lg:p-10 overflow-hidden bg-luxury">
          <div className="absolute -top-40 -right-40 w-[460px] h-[460px] bg-yellow-500/20 blur-[140px] rounded-full" />
          <div className="absolute -bottom-40 -left-40 w-[460px] h-[460px] bg-purple-500/20 blur-[140px] rounded-full" />
          <div className="relative flex flex-col md:flex-row md:items-end justify-between gap-6">
            <div>
              <Badge className="bg-gradient-to-r from-purple-500/20 to-yellow-500/20 text-yellow-300 border-yellow-500/30 mb-3">
                <Briefcase size={12} className="ml-1" /> {t("دليل الخدمات", "Services Directory")}
              </Badge>
              <h2 className="text-3xl lg:text-5xl font-bold mb-2 leading-tight text-luxury">
                {t("أكبر دليل عربي للخدمات في أمريكا", "Largest Arab services directory in America")}
              </h2>
              <p className="text-slate-400 max-w-2xl">
                {t("محامون، أطباء، معلمون، صنايعية، طهاة منزليون — تواصل مع محترفين موثوقين من المجتمع العربي.", "Lawyers, doctors, teachers, handymen, home chefs — connect with trusted Arab community pros.")}
              </p>
            </div>
            <Button onClick={() => navigate("/services/mine")} className="btn-primary" data-testid="my-provider-btn">
              <User className="ml-2" size={18} /> {t("صفحتي كمقدم خدمة", "My provider profile")}
            </Button>
          </div>
        </div>

        {/* Category chips */}
        <div className="flex gap-2 overflow-x-auto pb-1">
          <button onClick={() => { const f = {...filters, category: "", profession: ""}; setFilters(f); load(f); }}
            className={`px-4 py-2 rounded-full text-sm whitespace-nowrap ${filters.category === "" ? "bg-yellow-500 text-black" : "bg-slate-900 text-slate-300"}`}>
            {t("الكل", "All")}
          </button>
          {SERVICE_CATEGORIES.map((c) => (
            <button key={c.v}
              onClick={() => { const f = {...filters, category: c.v, profession: ""}; setFilters(f); load(f); }}
              className={`px-4 py-2 rounded-full text-sm whitespace-nowrap ${filters.category === c.v ? "bg-yellow-500 text-black" : "bg-slate-900 text-slate-300"}`}
              data-testid={`cat-${c.v}`}>
              {language === "ar" ? c.ar : c.en}
            </button>
          ))}
        </div>

        {/* Profession sub-filter when category selected */}
        {cat && (
          <div className="flex gap-2 overflow-x-auto pb-1">
            <button onClick={() => { const f = {...filters, profession: ""}; setFilters(f); load(f); }}
              className={`px-3 py-1 rounded-full text-xs whitespace-nowrap ${filters.profession === "" ? "bg-purple-500 text-white" : "bg-slate-800 text-slate-300"}`}>
              {t("كل التخصصات", "All specialties")}
            </button>
            {cat.subs.map((s) => (
              <button key={s}
                onClick={() => { const f = {...filters, profession: s}; setFilters(f); load(f); }}
                className={`px-3 py-1 rounded-full text-xs whitespace-nowrap ${filters.profession === s ? "bg-purple-500 text-white" : "bg-slate-800 text-slate-300"}`}>
                {subLabel(s, language)}
              </button>
            ))}
          </div>
        )}

        {/* Search bar */}
        <div className="glass-card rounded-2xl p-4 grid grid-cols-2 md:grid-cols-6 gap-3">
          <input className={`${inputCls} md:col-span-2`} placeholder={t("ابحث عن مقدّم خدمة...", "Search providers...")}
            value={filters.query} onChange={(e) => setFilters({...filters, query: e.target.value})}
            onKeyDown={(e) => e.key === "Enter" && load()} data-testid="prov-filter-query" />
          <select className={inputCls} value={filters.state} onChange={(e) => setFilters({...filters, state: e.target.value})} data-testid="prov-filter-state">
            <option value="">{t("كل الولايات", "All states")}</option>
            {US_STATES.map((s) => <option key={s.code} value={s.code}>{language === "ar" ? s.name_ar : s.name_en}</option>)}
          </select>
          <input className={inputCls} placeholder={t("المدينة", "City")} value={filters.city}
            onChange={(e) => setFilters({...filters, city: e.target.value})} data-testid="prov-filter-city" />
          <input className={inputCls} placeholder={t("ZIP", "ZIP")} value={filters.zip_code}
            onChange={(e) => setFilters({...filters, zip_code: e.target.value})} data-testid="prov-filter-zip" />
          <Button onClick={() => load()} className="btn-primary" data-testid="prov-search-btn">
            <Search size={16} className="ml-2" /> {t("بحث", "Search")}
          </Button>
        </div>

        {/* Featured */}
        {featured.length > 0 && (
          <div>
            <h3 className="text-xl font-bold text-white mb-3 flex items-center gap-2">
              <Award className="text-yellow-500" size={20} /> {t("مقدمون مميزون", "Featured Providers")}
            </h3>
            <div className="flex gap-4 overflow-x-auto pb-2">
              {featured.map((p) => (
                <div key={p.provider_id} className="min-w-[280px] max-w-[280px]">
                  <ProviderCard p={p} lang={language} t={t}
                    isSaved={savedIds.has(p.provider_id)} onSaveToggle={toggleSave}
                    onClick={() => navigate(`/services/${p.provider_id}`)} />
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Results */}
        <div>
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-xl font-bold text-white">
              {t("النتائج", "Results")} <span className="text-sm text-slate-400 font-normal">({providers.length})</span>
            </h3>
          </div>
          {loading ? (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {[1,2,3].map((i) => <div key={i} className="glass-card rounded-2xl h-72 skeleton" />)}
            </div>
          ) : providers.length === 0 ? (
            <div className="glass-card rounded-2xl p-12 text-center">
              <Briefcase className="text-slate-600 mx-auto mb-3" size={40} />
              <p className="text-slate-400 mb-4">{t("لا يوجد مقدمو خدمات مطابقون", "No providers match")}</p>
              <Button onClick={() => navigate("/services/mine")} className="btn-primary">
                {t("سجّل نفسك كمقدم خدمة", "Become a provider")}
              </Button>
            </div>
          ) : (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {providers.map((p) => (
                <ProviderCard key={p.provider_id} p={p} lang={language} t={t}
                  isSaved={savedIds.has(p.provider_id)} onSaveToggle={toggleSave}
                  onClick={() => navigate(`/services/${p.provider_id}`)} />
              ))}
            </div>
          )}
        </div>
      </div>
    </DashboardLayout>
  );
}
